import { VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import CollapsibleView from "@components/molecules/CollapsibleView";
import LabelView from "@components/molecules/LabelView";
import Locations from "@components/molecules/Locations/LocationModal";
import { useTypographyStore } from "@redux/slices/typographySlice";
import RenderSocialMediaView from "@scenes/findMatch/Utils/RenderSocialMediaView/RenderSocialMediaView";
import {
  EventName,
  EventType,
  getAnalyticsProductData,
} from "@utils/analytics";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, Platform, Pressable } from "react-native";
import { useTheme } from "styled-components/native";
import styled, { DefaultTheme } from "styled-components/native";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { ProductDetailProps } from "../types";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { CustomtOption, PdpSections } from "@utils/customization";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { VDBIcons } from "@utils/VDBIcons";
import { getProductProperty } from "@scenes/findMatch/Utils/ProductDetailList/utils";
import {
  dataMapValidateValue,
  getLocationProperty,
  reorderForColumnFirst,
} from "./utils";
import RenderHeaderView from "./HeaderView";
import {
  getProductDetailsArray,
  getProductGemstoneDetailsArray,
  getProductJewelryDetailsArray,
  getSupplierArray,
} from "./defaultData";
import { ProductCategory } from "@utils/constants";
import { useFilteredOptions } from "../../../hooks/useFilteredOptions";
import { useSelector } from "react-redux";
import { isPair, isParcel } from "@utils/product/productValidators";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { useAppSettings } from "@redux/slices/globalSlice";

const ProductDetailExpand = ({
  itemDetail,
  title,
  isProductDetail,
  productColor,
  isPdpScreen = false,
  productType,
  isFindAMatch,
  showQuantityInHandAsText,
}: ProductDetailProps) => {
  const [isSelectLocationOpen, setIsSelectLocationOpen] = useState(false);
  const [showNumber, setShowPhoneNumber] = useState(false);
  const [moreNumber, setMoreNumber] = useState(true);
  const [localSelectedLocation, setLocalSelectedLocation] = useState(null);
  const [customOptions, setCustomOptions] = useState<CustomtOption[]>([]);
  const userInfo = useUserInfo();
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const { trackButtonEvent } = useUserAnalytics() ?? {};
  const getAppSettings = useAppSettings();
  const hideCertOnMatchPair = getAppSettings?.hideCertOnMatchPair ?? false;

  const isSingleRowOptionOrder =
    theme.isLargeScreen &&
    isPdpScreen &&
    isProductDetail &&
    [
      ProductCategory.DIAMOND,
      ProductCategory.LAB_GROWN_DIAMOND,
      ProductCategory.GEMSTONE,
    ].includes(productType as ProductCategory)
      ? true
      : false;

  const priceMode = useMemo(
    () => userInfo?.user?.price_mode,
    [userInfo?.user?.price_mode]
  );

  const stoneType = isPair(itemDetail)
    ? SearchFilterTypeEnum.PAIRS
    : isCustomApp && isParcel(itemDetail)
    ? SearchFilterTypeEnum.PARCEL
    : SearchFilterTypeEnum.SINGLE_STONES;

  const sectionLayout = useGetPdpSectionLayout(
    isFindAMatch
      ? PdpSections.MATCH_PAIR_PRODUCT_DETAILS
      : PdpSections.PRODUCT_DETAILS,
    productType,
    stoneType
  );

  const productDetailsArray = getProductDetailsArray(itemDetail, productType);

  const productJewelryDetailsArray = getProductJewelryDetailsArray(itemDetail);

  const productGemstoneDetailsArray =
    getProductGemstoneDetailsArray(itemDetail);

  const SupplierArray = getSupplierArray(
    localSelectedLocation,
    showNumber,
    isCustomApp
  );

  const sectionSupplierLayout = useGetPdpSectionLayout(
    isFindAMatch
      ? PdpSections.MATCH_PAIR_PRODUCT_DETAILS
      : PdpSections.SUPPLIER_DETAILS,
    productType
  );

  const getCustomArray = useCallback(() => {
    if (customOptions.length === 0) {
      if (isProductDetail) {
        return productType === ProductCategory.JEWELRY
          ? productJewelryDetailsArray
          : productType === ProductCategory.GEMSTONE
          ? productGemstoneDetailsArray
          : productDetailsArray;
      } else {
        return SupplierArray;
      }
    }

    const isDataMappedValuePresent = (dataProperty: string) => {
      const dataMapedValue = getProductProperty(
        itemDetail?.[dataProperty as keyof typeof itemDetail],
        ""
      );
      return dataMapedValue !== "-";
    };

    const filteredSideData = customOptions.filter((item) =>
      item.name.includes("side_stone")
    );
    const filteredCenterData = customOptions.filter((item) =>
      item.name.includes("center_")
    );

    let isSideStoneValuePresent = false;
    let isCenterStoneValuePresent = false;

    if (productType === ProductCategory.JEWELRY) {
      filteredSideData.forEach((item) => {
        if (isDataMappedValuePresent(item.name)) {
          isSideStoneValuePresent = true;
        }
      });

      filteredCenterData.forEach((item) => {
        if (isDataMappedValuePresent(item.name)) {
          isCenterStoneValuePresent = true;
        }
      });
    }

    let updatedCustomOptions = customOptions;

    if (productType === ProductCategory.JEWELRY && !isSideStoneValuePresent) {
      updatedCustomOptions = updatedCustomOptions.filter(
        (item) => !item.name.includes("side_stone")
      );
    }

    if (productType === ProductCategory.JEWELRY && !isCenterStoneValuePresent) {
      updatedCustomOptions = updatedCustomOptions.filter(
        (item) => !/center_|centerstone/i.test(item.name)
      );
    }

    if (
      productType === ProductCategory.JEWELRY &&
      (!isCenterStoneValuePresent || !isSideStoneValuePresent)
    ) {
      updatedCustomOptions = updatedCustomOptions.filter(
        (item) => !item.type.includes("space")
      );
    }
    return updatedCustomOptions.map((option, index) => ({
      id: index + 1,
      title: t(option?.label),
      description:
        option?.type === "space" || option?.type === "title"
          ? ""
          : isProductDetail
          ? dataMapValidateValue(
              option?.name,
              productType,
              itemDetail,
              isPdpScreen,
              option?.type,
              moreNumber,
              showQuantityInHandAsText,
              t,
              hideCertOnMatchPair,
              option?.postfix
            )
          : getLocationProperty(
              localSelectedLocation,
              option?.name,
              option?.prefix_key ?? ""
            ),
      icon: option?.icon.data,
      showNumber:
        isProductDetail === false
          ? dataMapValidateValue(
              option?.name,
              productType,
              localSelectedLocation,
              isPdpScreen,
              option?.type,
              moreNumber,
              showQuantityInHandAsText,
              t,
              hideCertOnMatchPair,
              option?.postfix
            ) === "-"
            ? false
            : true
          : productType === ProductCategory.JEWELRY &&
            dataMapValidateValue(option?.name, productType, itemDetail) ===
              "-" &&
            option?.type !== "space" &&
            option?.type !== "title"
          ? option?.name?.trim() === "quantity_in_hand"
            ? !!itemDetail?.quantity_in_hand // true only if quantity_in_hand exists
            : false
          : true,
      price_modes: option.price_modes,
      order: isSingleRowOptionOrder
        ? option?.webOrder || option.order
        : option.order || 0,
      isTitle: option?.type === "title" ? true : false,
      isSingleRow: option?.isSingleRow,
      clickable: option?.clickable ?? false,
      color: option?.color ?? "",
      clickToShow: option?.type === "clickToShow" ? true : false,
    }));
  }, [
    customOptions?.length,
    showNumber,
    itemDetail,
    SupplierArray,
    localSelectedLocation,
    title,
  ]);

  const filteredOptions = useFilteredOptions(theme, priceMode, sectionLayout);

  const filteredSupplierOptions = useFilteredOptions(
    theme,
    priceMode,
    sectionSupplierLayout
  );
  const isSingleRowOption = useMemo(() => {
    if (!isProductDetail) return false;
    if (!filteredOptions?.length) return null;
    return (
      filteredOptions.length >= 4 &&
      theme.isLargeScreen &&
      isPdpScreen &&
      isProductDetail &&
      [
        ProductCategory.DIAMOND,
        ProductCategory.LAB_GROWN_DIAMOND,
        ProductCategory.GEMSTONE,
      ].includes(productType)
    );
  }, [filteredOptions, theme, isPdpScreen, isProductDetail, productType]);

  useEffect(() => {
    if (isProductDetail) {
      setCustomOptions(filteredOptions);
    } else {
      setCustomOptions(filteredSupplierOptions);
    }
  }, [isProductDetail, filteredOptions, filteredSupplierOptions]);

  const handleShowNumber = () => {
    setShowPhoneNumber(true);
    setMoreNumber(false);
    trackButtonEvent?.(
      EventName.DETAILS_CONTACT_BUTTON_CLICK,
      EventType.DETAIL_CONTACT_BUTTON,
      getAnalyticsProductData(itemDetail)
    );
  };

  const handlePhoneClick = useCallback(() => {
    trackButtonEvent?.(
      EventName.CONTACT_VENDOR_THROUGH_CALL,
      EventType.CONTACT_VENDOR_THROUGH_CALL,
      {
        ...getAnalyticsProductData(itemDetail),
        button_click_type: "link",
        button_description: "contact_vendor_through_call",
        button_name: "contact_vendor_through_call",
      }
    );
  }, [itemDetail, trackButtonEvent]);

  const keyExtractor = (item: any, index: number) => {
    if (item?.id) {
      const sectionIdentifier =
        (item as any).sectionTitle || (item as any).type || "";
      const uniqueId = (item as any).fav_id || item.id || index;
      return `${sectionIdentifier}-${uniqueId}-${index}`;
    }
    return index + "-item";
  };

  useEffect(() => {
    if (itemDetail && Array.isArray(itemDetail.locations)) {
      const primaryLocation = itemDetail.locations.find(
        (item) => item.is_primary
      );
      setLocalSelectedLocation(primaryLocation);
    }
  }, [itemDetail]);

  const changeNewLocation = (selectedLocation: any) => {
    setLocalSelectedLocation(selectedLocation);
  };

  const formattedData = useMemo(() => {
    return reorderForColumnFirst(getCustomArray(), isSingleRowOption ? 2 : 1);
  }, [getCustomArray, isSingleRowOption, itemDetail]);

  React.useEffect(() => {
    if (formattedData.some((item: any) => item.clickToShow) && moreNumber) {
      setShowPhoneNumber(false);
    } else if (isCustomApp && filteredSupplierOptions?.options === undefined) {
      setShowPhoneNumber(true);
    }
  }, [JSON.stringify(formattedData)]);

  const RenderShowNumberView = () => (
    <Pressable
      onPress={() => {
        handleShowNumber();
      }}
    >
      <RowContainer>
        <VDBText
          type={typography["Body-M"]}
          color={theme[productColor as keyof DefaultTheme] as string}
        >
          {t("productDetailPage.clickToShowNumber.text")}
        </VDBText>
        <VDBIcon
          name={VDBIcons.ARROW_DOWN}
          size={24}
          color={theme["core-04"]}
          variant={IconsVariant.VDBCustom}
        />
      </RowContainer>
    </Pressable>
  );

  const ItemCard = ({ item }: any) => (
    <>
      {item?.showNumber === undefined || item?.showNumber === true ? (
        <CustomView
          isSingleRowOption={isSingleRowOption}
          {...(isSingleRowOption && {
            style: item?.isSingleRow ? { width: "100%" } : { width: "48%" },
          })}
        >
          <LabelView
            itemLeftText={item.title}
            itemRightText={item.description ?? "-"}
            ellipsize={false}
            rightPrefixTextTheme={
              item.color
                ? (theme[item.color as keyof DefaultTheme] as string)
                : theme["core-05"]
            }
            leftPostfixText=""
            itemLeftTextTheme={
              item.isTitle && item.isTitle === true
                ? theme["core-05"]
                : theme["core-06"]
            }
            isFixedKeyWidth={true}
            cellWidth={theme.isMobile ? 250 : 200}
            keyTypographyStyle={
              item.isTitle && item.isTitle === true
                ? typography["Body-M"]
                : typography["Body-R"]
            }
            valueTypographyStyle={typography["Body-M"]}
            clickableHandle={item?.clickable}
            emailClickable={item?.emailClickable}
            keyWidthPercent={!theme.isLargeScreen ? 40 : undefined}
            keyWidth={
              theme.isLargeScreen ? (!item?.isSingleRow ? 85 : 120) : 50
            }
            marginRight={theme.isMobile ? 50 : 0}
            alignRightLabel={"flex-start"}
            isFixWidth
            onPhoneClick={item?.clickable ? handlePhoneClick : undefined}
          />
        </CustomView>
      ) : null}

      {item?.clickToShow === true && !showNumber && (
        <>
          <RenderShowNumberView />
        </>
      )}
    </>
  );

  const ShowHeartArrowCard = () => (
    <>
      <HeartView>
        <VDBIcon
          name={VDBIcons.HEARTS_AND_ARROWS}
          size={24}
          color={theme["error-01"]}
          variant={IconsVariant.VDBCustom}
        />
        <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
          {t("productDetailPage.heartsarrows.text")}
        </VDBText>
      </HeartView>
    </>
  );

  const showClickToShowCustomApp = () => {
    const clickToShowOption = Object.values(
      sectionSupplierLayout?.options || {}
    ).find((item: any) => item.name === "clickToShow");
    if (!clickToShowOption) return false;
    return clickToShowOption.price_modes.includes(priceMode);
  };
  if (isSingleRowOption === null) {
    return null;
  }

  return (
    <>
      <CollapsibleView
        initExpanded={true}
        title={title}
        arrowStyling={{ color: theme["core-04"] }}
        applyShadow
      >
        <Divider />
        <Container>
          <FlatList
            data={formattedData}
            extraData={formattedData}
            renderItem={({ item }) => <ItemCard item={item} />}
            keyExtractor={keyExtractor}
            scrollEnabled={Platform.OS === "web" ? true : false}
            {...(isSingleRowOption && {
              numColumns: theme.isLargeScreen ? 2 : 1,
              columnWrapperStyle: theme.isLargeScreen
                ? { flexWrap: "wrap", justifyContent: "space-between" }
                : undefined,
            })}
            ListHeaderComponent={
              !isProductDetail && itemDetail?.locations?.length !== 1
                ? RenderHeaderView(
                    setIsSelectLocationOpen,
                    isSelectLocationOpen,
                    localSelectedLocation,
                    itemDetail,
                    theme,
                    t,
                    typography
                  )
                : undefined
            }
            ListFooterComponent={
              !isProductDetail &&
              !showNumber &&
              (!isCustomApp ? true : showClickToShowCustomApp()) ? (
                <>
                  <RenderShowNumberView />
                </>
              ) : !isProductDetail && showNumber ? (
                <RenderSocialMediaView
                  contact_whatsapp={localSelectedLocation?.contact_whatsapp}
                  contact_iphone={localSelectedLocation?.contact_iphone}
                  contact_whatsapp_country_code={
                    localSelectedLocation?.contact_whatsapp_country_code
                  }
                  contact_iphone_country_code={
                    localSelectedLocation?.contact_iphone_country_code
                  }
                  hideDivider={true}
                />
              ) : undefined
            }
          />

          {isProductDetail &&
            itemDetail?.hearts_and_arrows === "Hearts and Arrows" && (
              <ShowHeartArrowCard />
            )}
        </Container>
      </CollapsibleView>

      <Locations
        isOpen={isSelectLocationOpen}
        onClose={() => setIsSelectLocationOpen(false)}
        languages={itemDetail?.locations}
        selectedLocation={localSelectedLocation}
        changeLocation={changeNewLocation}
      />
    </>
  );
};

const Container = styled.View`
  padding: 16px;
`;

const CustomView = styled.View<{ isSingleRowOption?: boolean }>`
  justify-content: flex-start;
  margin-vertical: 4px;
  margin-vertical: ${({ isSingleRowOption }) =>
    isSingleRowOption ? "4px" : "8px"};
`;

const HeartView = styled.View`
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-vertical: 6px;
  padding-top: 6px;
  gap: 8px;
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

const RowContainer = styled.View`
  flex-direction: row;
  margin-top: 22px;
  align-items: center;
`;

export default ProductDetailExpand;
